import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Close from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    TextField,
    Typography,
} from "@vapor/react-material";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DocLabelUpdateAction } from "../../../actions/labels";
import { OrderLabel } from "../../../interfaces/label";
import Tag from "../../vapor/tag";
import { TagColor } from "../../vapor/tag/colors";
import "./style.scss";

interface ManageDocLabelsDialogProps {
    onSave: (actions: Array<DocLabelUpdateAction>) => void;
    docLabels: Array<OrderLabel>;
    presetLabels?: Array<OrderLabel>;
}

const ManageDocLabelDialog = (
    props: ManageDocLabelsDialogProps,
): JSX.Element => {
    const intl = useIntl();

    const [open, setOpen] = useState(false);

    // Internal state to manage dialog labels
    const [localDocLabels, setLocalDocLabels] = useState<OrderLabel[]>(
        props.docLabels,
    );
    const docLabelsKeys = (localDocLabels || []).map((l) => l.name);

    // Actions to update index document
    const [docLabelActions, setDocLabelActions] = useState<
        Array<DocLabelUpdateAction>
    >([]);

    const handleRemoveLabel = (label: string) => {
        setDocLabelActions([
            ...docLabelActions,
            {
                type: "remove",
                labelName: label,
            },
        ]);
        setLocalDocLabels(localDocLabels.filter((l) => l.name !== label));
    };
    const handleClose = () => {
        setOpen(false);
        setDocLabelActions([]);
    };

    const [editing, setEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    useEffect(() => {
        setLocalDocLabels(props.docLabels);
    }, [props.docLabels]);

    return (
        <>
            <div
                className="bis-label-add-wrapper"
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="bis-cards-order-labelbox-add"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(true);
                    }}
                >
                    <FontAwesomeIcon icon={["far", "tags"]} />
                </div>
            </div>
            <Dialog
                open={open}
                maxWidth="sm"
                fullWidth
                sx={{
                    "& .MuiDialog-paper": {
                        height: "80vh",
                        maxHeight: "80vh",
                    },
                }}
            >
                <DialogTitle>
                    Etichette Documento
                    <IconButton color="primary" onClick={() => handleClose()}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        overflowY: "auto",
                    }}
                >
                    <>
                        <Autocomplete
                            className="bis-cards-order-labelbox-input"
                            options={
                                props.presetLabels?.filter(
                                    (l) => !docLabelsKeys.includes(l.name),
                                ) || []
                            }
                            getOptionLabel={(option: any) => {
                                return option.name;
                            }}
                            freeSolo={false}
                            disableCloseOnSelect={false}
                            onChange={(_event, value: any) => {
                                console.log(value);
                                const addedLabel = value;
                                if (addedLabel) {
                                    setDocLabelActions([
                                        ...docLabelActions,
                                        {
                                            type: "add",
                                            labelName: addedLabel.name,
                                        },
                                    ]);
                                    setLocalDocLabels([
                                        ...localDocLabels,
                                        addedLabel,
                                    ]);
                                }
                                setEditing(false);
                                inputRef.current?.blur();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    setEditing(false);
                                }
                            }}
                            renderOption={(optProps, option: any) => (
                                <li
                                    {...optProps}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        inputRef.current?.blur();
                                        setLocalDocLabels([
                                            ...localDocLabels,
                                            {
                                                name: option.name,
                                                color:
                                                    props.presetLabels?.find(
                                                        (l) =>
                                                            l.name ===
                                                            option.name,
                                                    )?.color ||
                                                    TagColor.RustyRed,
                                            },
                                        ]);
                                        setDocLabelActions([
                                            ...docLabelActions,
                                            {
                                                type: "add",
                                                labelName: option.name,
                                            },
                                        ]);
                                    }}
                                >
                                    {option.name}
                                </li>
                            )}
                            renderInput={(params: any) => (
                                <TextField
                                    {...params}
                                    inputRef={inputRef}
                                    placeholder={intl.formatMessage({
                                        id: "general.addLabel",
                                    })}
                                />
                            )}
                        />
                        {localDocLabels.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 2 }}
                            >
                                Nessuna etichetta aggiunta.
                            </Typography>
                        ) : (
                            <List>
                                {localDocLabels.map((label) => (
                                    <ListItem
                                        key={label.name}
                                        sx={{
                                            "&:focus-visible": {
                                                border: "none",
                                                outline: "none",
                                            },
                                            "&:hover": {
                                                border: "none",
                                                outline: "none",
                                            },
                                        }}
                                    >
                                        <>
                                            <Tag color={label.color}>
                                                {label.name}
                                            </Tag>
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    color="error"
                                                    onClick={() =>
                                                        handleRemoveLabel(
                                                            label.name,
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            props.onSave(docLabelActions);
                            handleClose();
                        }}
                    >
                        <FormattedMessage id="actions.save" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ManageDocLabelDialog;
